import { useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { colorTokens } from "../../theme/tokens";
import { useContextMenu } from "../../hooks/useContextMenu";
import type { RowAction } from "../../utils/explorerActions";

export interface FileTreeFolder {
  key: string;
  label: string;
}

interface NodeState {
  expanded: boolean;
  loading: boolean;
  /** null: not yet loaded. */
  children: string[] | null;
}

interface VisibleItem {
  /** null only for the root "Your Files" item. */
  folderKey: string | null;
  /** Path within folderKey; "" at its own root. */
  path: string;
  depth: number;
  label: string;
}

function nodeKey(folderKey: string, path: string): string {
  return `${folderKey} ${path}`;
}

function childPath(parentPath: string, name: string): string {
  return parentPath ? `${parentPath}/${name}` : name;
}

export interface FileTreeProps {
  /** The top-level folders (PARTNER_TO_LEEFT/LEEFT_TO_PARTNER, or DELIVERED/LEEFT_TO_PARTNER/REJECTED); their own path is always "". */
  folders: FileTreeFolder[];
  /** null selects the root ("Your Files") itself. */
  selectedFolder: string | null;
  /** ADR 0010: the path within selectedFolder; "" at its root. */
  selectedPath: string;
  onSelectRoot: () => void;
  onSelect: (folder: string, path: string) => void;
  /**
   * ADR 0010: lazily loads a node's immediate subfolder names when it is
   * first expanded. `path` is "" for a top-level folder's own children.
   */
  loadChildren: (folder: string, path: string) => Promise<string[]>;
  rootLabel?: string;
  /**
   * Right-click actions for a node (Open, Upload here / New folder /
   * Delete folder where allowed, Refresh), from the same shared action
   * model as the table and toolbar menus. `folder` is null for the root.
   * Omitted or empty means no context menu for that item.
   */
  actionsForNode?: (folder: string | null, path: string) => RowAction[];
  /** Any lazily-loaded children are dropped when this value changes (ADR 0010: "dropped on refresh"). */
  refreshKey?: unknown;
}

/**
 * The explorer sidebar (files-explorer-reference.png): a root node, its
 * top-level folders, and their subfolders loaded lazily as each node is
 * expanded (ADR 0010). WAI-ARIA tree view pattern: role="tree", treeitem,
 * nested role="group", aria-expanded, aria-level, aria-selected on the
 * current item, and arrow-key navigation (Up/Down move focus, Right
 * expands or moves into the first child, Left collapses or moves to the
 * parent, Enter/Space selects) over the flattened list of visible items
 * (a simplified roving tabindex: only the flattened index matters, not a
 * per-branch one).
 */
export function FileTree({
  folders,
  selectedFolder,
  selectedPath,
  onSelectRoot,
  onSelect,
  loadChildren,
  rootLabel = "Your Files",
  actionsForNode,
  refreshKey,
}: FileTreeProps) {
  const [rootExpanded, setRootExpanded] = useState(true);
  const [nodes, setNodes] = useState<Map<string, NodeState>>(new Map());
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const menu = useContextMenu();
  const [menuTarget, setMenuTarget] = useState<{ folder: string | null; path: string } | null>(null);

  // ADR 0010: lazily-loaded children are cached per path, dropped on
  // refresh, without an Effect (React docs: "Adjusting state when a prop
  // changes").
  const [previousRefreshKey, setPreviousRefreshKey] = useState(refreshKey);
  if (refreshKey !== previousRefreshKey) {
    setPreviousRefreshKey(refreshKey);
    setNodes(new Map());
  }

  function getNode(folderKey: string, path: string): NodeState | undefined {
    return nodes.get(nodeKey(folderKey, path));
  }

  async function toggleExpand(folderKey: string, path: string) {
    const key = nodeKey(folderKey, path);
    const current = nodes.get(key);
    if (current?.expanded) {
      setNodes((prev) => new Map(prev).set(key, { ...current, expanded: false }));
      return;
    }
    if (current?.children) {
      setNodes((prev) => new Map(prev).set(key, { ...current, expanded: true }));
      return;
    }
    setNodes((prev) => new Map(prev).set(key, { expanded: true, loading: true, children: null }));
    try {
      const children = await loadChildren(folderKey, path);
      setNodes((prev) => new Map(prev).set(key, { expanded: true, loading: false, children }));
    } catch {
      setNodes((prev) => new Map(prev).set(key, { expanded: true, loading: false, children: [] }));
    }
  }

  // Flattens the currently visible items (root, top-level folders and any
  // expanded descendants) for keyboard navigation and context menus.
  const visible: VisibleItem[] = [{ folderKey: null, path: "", depth: 0, label: rootLabel }];
  if (rootExpanded) {
    for (const folder of folders) {
      visible.push({ folderKey: folder.key, path: "", depth: 1, label: folder.label });
      walkChildren(folder.key, "", 2);
    }
  }
  function walkChildren(folderKey: string, path: string, depth: number) {
    const state = getNode(folderKey, path);
    if (!state?.expanded || !state.children) {
      return;
    }
    for (const name of state.children) {
      const nextPath = childPath(path, name);
      visible.push({ folderKey, path: nextPath, depth, label: name });
      walkChildren(folderKey, nextPath, depth + 1);
    }
  }

  function focusItem(index: number) {
    const clamped = Math.max(0, Math.min(visible.length - 1, index));
    itemRefs.current[clamped]?.focus();
  }

  function isSelected(item: VisibleItem): boolean {
    if (item.folderKey === null) {
      return selectedFolder === null;
    }
    return selectedFolder === item.folderKey && selectedPath === item.path;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number, item: VisibleItem) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusItem(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusItem(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        if (item.folderKey === null) {
          if (rootExpanded) {
            focusItem(index + 1);
          } else {
            setRootExpanded(true);
          }
        } else {
          const state = getNode(item.folderKey, item.path);
          if (state?.expanded) {
            focusItem(index + 1);
          } else {
            void toggleExpand(item.folderKey, item.path);
          }
        }
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (item.folderKey === null) {
          setRootExpanded(false);
        } else {
          const state = getNode(item.folderKey, item.path);
          if (state?.expanded) {
            void toggleExpand(item.folderKey, item.path);
          } else {
            focusItem(index - 1);
          }
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (item.folderKey === null) {
          onSelectRoot();
        } else {
          onSelect(item.folderKey, item.path);
        }
        break;
      default:
        break;
    }
  }

  function handleContextMenu(event: MouseEvent<HTMLDivElement>, item: VisibleItem) {
    const actions = actionsForNode?.(item.folderKey, item.path) ?? [];
    if (actions.length === 0) {
      return;
    }
    setMenuTarget({ folder: item.folderKey, path: item.path });
    menu.open(event);
  }

  const menuActions = menuTarget ? (actionsForNode?.(menuTarget.folder, menuTarget.path) ?? []) : [];

  return (
    <Box role="tree" aria-label={rootLabel} sx={{ py: 1 }}>
      {visible.map((item, index) => {
        const selected = isSelected(item);
        const state = item.folderKey !== null ? getNode(item.folderKey, item.path) : undefined;
        const expanded = item.folderKey === null ? rootExpanded : (state?.expanded ?? false);
        const isRoot = item.folderKey === null;
        return (
          <Box
            key={item.folderKey === null ? "root" : nodeKey(item.folderKey, item.path)}
            ref={(el: HTMLDivElement | null) => {
              itemRefs.current[index] = el;
            }}
            role="treeitem"
            tabIndex={index === 0 ? 0 : -1}
            aria-expanded={expanded}
            aria-selected={selected}
            aria-level={item.depth + 1}
            onClick={() => (isRoot ? onSelectRoot() : onSelect(item.folderKey!, item.path))}
            onKeyDown={(event) => handleKeyDown(event, index, item)}
            onContextMenu={(event) => handleContextMenu(event, item)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 2 + item.depth * 3,
              pr: 2,
              py: 1,
              cursor: "pointer",
              bgcolor: selected ? colorTokens.brandSubtle : "transparent",
              "&:hover": { bgcolor: colorTokens.brandSubtle },
              "&:focus-visible": { outline: `2px solid ${colorTokens.brand}`, outlineOffset: -2 },
            }}
          >
            <IconButton
              size="small"
              aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
              onClick={(event) => {
                event.stopPropagation();
                if (isRoot) {
                  setRootExpanded((current) => !current);
                } else {
                  void toggleExpand(item.folderKey!, item.path);
                }
              }}
              sx={{ p: 0.25 }}
            >
              {state?.loading ? (
                <CircularProgress size={14} />
              ) : expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
            {isRoot && expanded ? (
              <FolderOpenIcon fontSize="small" sx={{ color: colorTokens.textMuted }} />
            ) : (
              <FolderIcon fontSize="small" sx={{ color: colorTokens.textMuted }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: isRoot ? 600 : 400 }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}

      <Menu
        open={menu.position !== null}
        onClose={menu.close}
        anchorReference="anchorPosition"
        anchorPosition={menu.position ?? undefined}
      >
        {menuActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              menu.close();
              action.onSelect();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
